import React, { useEffect, useState, useCallback } from 'react';
import { Modal, Form, Input, Select, Button, Row, Col, Switch, Upload, message } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { BASE_API } from '../../axios';
import { getUserDetails } from '../../utils/helper';
import { TokenStorage } from '../../utils/cookieStorage';
import axios from 'axios';

const { Option } = Select;

interface EditTemplateOodocsProps {
  visible: boolean;
  templateData: any;
  modalitiesList: any[];
  bodyPartsList: any[];
  onCancel: () => void;
  onSuccess: () => void;
}

const EditTemplateOodocs: React.FC<EditTemplateOodocsProps> = ({
  visible,
  templateData,
  modalitiesList,
  bodyPartsList,
  onCancel,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const [fileList, setFileList] = useState<any[]>([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (visible && templateData) {
      form.setFieldsValue({
        dt_template_name: templateData.dt_template_name,
        dt_display_name: templateData.dt_display_name,
        dt_modality: templateData.dt_modality,
        dt_bodypart: templateData.dt_bodypart,
        dt_comments: templateData.dt_comments,
        dt_active: templateData.dt_active === 'Y',
      });
      setFileList([]);
    } else if (!visible) {
      form.resetFields();
      setFileList([]);
    }
  }, [visible, templateData, form]);

  const handleSubmit = useCallback(async () => {
    try {
      await form.validateFields();
    } catch {
      return;
    }

    const values = form.getFieldsValue();
    const hasFile = fileList.length > 0;

    setSubmitting(true);
    try {
      let res;
      if (hasFile) {
        // Multipart form-data when file is included
        const formData = new FormData();
        formData.append('dt_id', String(templateData.dt_id));
        formData.append('dt_template_name', values.dt_template_name);
        formData.append('dt_display_name', values.dt_display_name);
        formData.append('dt_modality', values.dt_modality);
        formData.append('dt_bodypart', values.dt_bodypart);
        if (values.dt_comments) formData.append('dt_comments', values.dt_comments);
        formData.append('dt_active', values.dt_active ? 'Y' : 'N');
        formData.append('file', fileList[0].originFileObj || fileList[0]);

        const token = TokenStorage.getAccessToken();
        const csrfToken = sessionStorage.getItem('csrf_token');
        res = await axios.post(`${BASE_API}/update-template-oodocs`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            ...(csrfToken ? { 'x-csrf-token': csrfToken } : {}),
          },
          timeout: 60000,
        });
      } else {
        // JSON when no file replacement
        const token = TokenStorage.getAccessToken();
        const csrfToken = sessionStorage.getItem('csrf_token');
        res = await axios.post(`${BASE_API}/update-template-oodocs`, {
          dt_id: templateData.dt_id,
          dt_template_name: values.dt_template_name,
          dt_display_name: values.dt_display_name,
          dt_modality: values.dt_modality,
          dt_bodypart: values.dt_bodypart,
          dt_comments: values.dt_comments || '',
          dt_active: values.dt_active ? 'Y' : 'N',
        }, {
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            ...(csrfToken ? { 'x-csrf-token': csrfToken } : {}),
          },
          timeout: 30000,
        });
      }

      if (res.data?.success) {
        message.success('Template updated successfully');
        onSuccess();
      } else {
        message.error(res.data?.message || 'Failed to update template');
      }
    } catch (err: any) {
      message.error(err.response?.data?.message || err.message || 'Failed to update template');
    } finally {
      setSubmitting(false);
    }
  }, [form, fileList, templateData, onSuccess]);

  const handleCancel = useCallback(() => {
    form.resetFields();
    setFileList([]);
    onCancel();
  }, [form, onCancel]);

  const uploadProps = {
    beforeUpload: (file: any) => {
      const isDocx = file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
        || file.name?.endsWith('.docx');
      if (!isDocx) {
        message.error('Only .docx files are allowed');
        return Upload.LIST_IGNORE;
      }
      return false;
    },
    onChange: ({ fileList: newFileList }: any) => {
      setFileList(newFileList.slice(-1));
    },
    fileList,
    maxCount: 1,
    accept: '.docx',
  };

  const formatFileSize = (bytes: number) => {
    if (!bytes) return '-';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <Modal
      title="Edit Template (.docx)"
      open={visible}
      onCancel={handleCancel}
      width={800}
      footer={[
        <Button key="cancel" onClick={handleCancel} className="cancel-btn">Cancel</Button>,
        <Button key="submit" type="primary" onClick={handleSubmit} loading={submitting} className="submit-btn">
          Update Template
        </Button>,
      ]}
      className="edit-template-modal"
    >
      <Form form={form} layout="vertical" className="edit-template-form">
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Template Name"
              name="dt_template_name"
              rules={[{ required: true, message: 'Please enter template name!' }]}
            >
              <Input placeholder="Enter template name" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Display Name"
              name="dt_display_name"
              rules={[{ required: true, message: 'Please enter display name!' }]}
            >
              <Input placeholder="Enter display name" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Modality"
              name="dt_modality"
              rules={[{ required: true, message: 'Please select modality!' }]}
            >
              <Select placeholder="Select modality">
                {modalitiesList.map((m: any) => (
                  <Option key={m.code} value={m.code}>{m.label}</Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Body Part"
              name="dt_bodypart"
              rules={[{ required: true, message: 'Please select body part!' }]}
            >
              <Select placeholder="Select body part">
                {bodyPartsList.map((bp: any) => (
                  <Option key={bp.code} value={bp.code}>{bp.label}</Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={18}>
            <Form.Item label="Comments" name="dt_comments">
              <Input.TextArea rows={3} placeholder="Optional notes" maxLength={200} showCount />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item label="Active Status" name="dt_active" valuePropName="checked">
              <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item label="Replace Template File (.docx)">
          {templateData?.dt_file_size && (
            <div style={{ marginBottom: 8, color: '#666', fontSize: '0.85rem' }}>
              Current file: {formatFileSize(templateData.dt_file_size)}
            </div>
          )}
          <Upload {...uploadProps}>
            <Button icon={<UploadOutlined />}>Select New .docx File (optional)</Button>
          </Upload>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default EditTemplateOodocs;
